import { Constants } from './common/Constants';
import * as Enums from './common/Enumerations';
import { RuntimeScope } from './common/InternalTypes';
import { Logger } from './common/Logger';
import { LoggerTexts } from './common/LoggerTexts';
import { SurveyInvitationDialogCallback } from './common/Types';
import { CentralConfig } from './config/CentralConfig';
import { ContextDataController } from './data/ContextDataController';
import { PushContextData } from './data/PushContextData';
import { QualtricsBridge } from './QualtricsBridge';
import { StorageService } from './storage/StorageService';
import { InstantPushController } from './survey/InstantPushController';
import { PresentationController } from './survey/PresentationController';
import { TimedPushController } from './survey/TimedPushController';
import { ValidationHandler } from './survey/ValidationHandler';
import { Theming } from './theming/Theming';

export class ControllerProvider {
	private readonly _logger: Logger;
	private readonly _centralConfig: CentralConfig;
	private readonly _qualtricsBridge: QualtricsBridge;
	private readonly _contextDataController: ContextDataController;
	private _storageService: StorageService;
	private _validationHandler?: ValidationHandler;
	private _timedPushController?: TimedPushController;
	private _presentationController?: PresentationController;
	private _instantPushController?: InstantPushController;
	private _theming?: Theming;

	constructor(centralConfig: CentralConfig) {
		this._logger = new Logger(Constants.FILE_INIT_CONTROLLER);
		this._centralConfig = centralConfig;
		this._qualtricsBridge = new QualtricsBridge();
		this._contextDataController = new ContextDataController();
	}

	get centralConfig(): CentralConfig {
		return this._centralConfig;
	}
	get contextDataController(): ContextDataController {
		return this._contextDataController;
	}
	get qualtricsBridge(): QualtricsBridge {
		return this._qualtricsBridge;
	}
	get storageService(): StorageService {
		return this._storageService;
	}
	get validationHandler(): ValidationHandler | undefined {
		return this._validationHandler;
	}
	get timedPushController(): TimedPushController | undefined {
		return this._timedPushController;
	}
	get presentationController(): PresentationController | undefined {
		return this._presentationController;
	}
	get instantPushController(): InstantPushController | undefined {
		return this._instantPushController;
	}
	get theming(): Theming | undefined {
		return this._theming;
	}

	public initStorage() {
		this._storageService = new StorageService();
	}

	public initValidationHandler() {
		this._validationHandler = new ValidationHandler();
		this._validationHandler.init(this.storageService, this.centralConfig);
	}

	public initTimedPushController(runtimeScope: RuntimeScope) {
		if (runtimeScope.timedPush) {
			this._timedPushController = new TimedPushController(this.centralConfig, this.storageService);
			this._timedPushController.init(this._timedPushCallback.bind(this));
		} else {
			this._logger.info(LoggerTexts.CONTROLLER_PROVIDER.INIT_TIMED_PUSH_NOT_AVAILABLE);
		}
	}

	public initInstantPushController(runtimeScope: RuntimeScope) {
		if (runtimeScope.instantPush) {
			this._instantPushController = new InstantPushController(this.centralConfig, this.storageService);
			this._instantPushController.checkAndRemoveExpiredTriggeredDateTimes();
		} else {
			this._logger.info(LoggerTexts.CONTROLLER_PROVIDER.INIT_INSTANT_PUSH_NOT_AVAILABLE);
		}
	}

	public initPresentationController(surveyInvitationDialogCallback: SurveyInvitationDialogCallback) {
		this._presentationController = new PresentationController(
			this.storageService,
			this.centralConfig,
			this.qualtricsBridge,
			this.contextDataController,
			surveyInvitationDialogCallback
		);
	}

	public async initTheming() {
		if (this._centralConfig.themingConfig) {
			this._theming = new Theming();
			this._theming?.initialize(this._centralConfig.themingConfig, this._contextDataController);
		}
	}

	public destroy() {
		this._contextDataController.reset();
		this._qualtricsBridge.destroy();
		this._timedPushController?.destroy();
		this._presentationController?.destroy();
	}

	private async _timedPushCallback() {
		const pushContextData = new PushContextData(Constants.GENERIC_TRIGGER_AREAID, Enums.TRIGGER_TYPE.timed);
		if (this.presentationController) {
			await this.presentationController.displayInvitationDialog(Enums.CLIENT_ACTION.timedPushFeedback, pushContextData);
		}
	}
}
